import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, Gift, Award, Users, LogOut, ArrowLeft, Share2, MessageSquare, User, Edit, FileCheck, Phone, Send, Shield, AlertTriangle, CreditCard, Check, HelpCircle, Download, Smartphone, QrCode, KeyRound, Truck, Gamepad2, Radio, Cloud, Trophy, ChevronRight, ChevronDown, Volume2, VolumeX, BarChart3, Heart, ShoppingBag, ImagePlus, X, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { api } from '../services/api';
import { toast } from 'sonner';
import InstallPWAPrompt from '../components/InstallPWAPrompt';
import QRCodeCanvas from 'qrcode';
import QRCode from 'react-qr-code';
import { StoreImage } from '../components/OptimizedImage';
import ProfileEditor from '../components/ProfileEditor';
import ParentalConsentModal, { DownloadConsentButton } from '../components/ParentalConsentModal';
import { TOTPLoginVerification, TOTPSetupModal } from '../components/TOTPVerification';
import ReadOnlyLoyaltyCard from '../components/ReadOnlyLoyaltyCard';
import SecureVault from '../components/SecureVault';
import GamesHub from '../components/GamesHub';
import LPEChat, { ChatButton } from '../components/LPEChat';
import RadioPlayer from '../components/RadioPlayer';
import WeatherWidget from '../components/WeatherWidget';
import CustomerLeaderboard from '../components/CustomerLeaderboard';
import FlashOffers from '../components/FlashOffers';
import PollsWidget from '../components/PollsWidget';
import NewsFeed from '../components/NewsFeed';
import EventsCalendar from '../components/EventsCalendar';
import BirthdayGift, { BirthdayCountdown } from '../components/BirthdayGift';
import TVPlayer from '../components/TVPlayer';
import DeliveryOrderModal from '../components/DeliveryOrderModal';
import MaxDeliveryAssistant from '../components/MaxDeliveryAssistant';
import { ReferralShowcase } from '../components/ReferralShowcase';
import { ThemeToggleMini, ThemeSelector, ThemeIndicator, useTheme, themeClasses } from '../components/ThemeToggle';
import Footer from '../components/Footer';
import { NotificationToggle } from '../components/NotificationPrompt';
import AppsHub from '../components/AppsHub';
import { StripePayment } from '../components/StripePayment';
import { Heartbeats } from '../components/Heartbeats';
import { useAnalytics } from '../hooks/useAnalytics';
import { useClientAuth } from '../hooks/useClientAuth';
import getPositiveMessage, { extractFirstName } from '../utils/positiveMessages';
import { welcomeConfetti, victoryConfetti, launchBadgeConfetti, matchConfetti } from '../utils/confetti';
import soundSystem from '../utils/sounds';
import { useBadgeDetection, useStampSound, useMatchSound } from '../utils/badgeEffects';
import { PersonalStats, BadgesGrid } from '../components/CustomerStats';

// 🧲 Système d'accroche magnétique Heartbeats
import { useMagneticHook } from '../hooks/heartbeats/useMagneticHook';
import { MagneticButton } from '../components/heartbeats/MagneticButton';
import { RetentionPopup } from '../components/heartbeats/RetentionPopup';
import { PixieInfiltrator } from '../components/heartbeats/soul/PixieInfiltrator';
import SasPassager from '../components/SasPassager';

// 🛒 COMPOSANTS REFACTORISÉS DU PORTAL CLIENT
import WelcomeMessage from '../components/client-portal/WelcomeMessage';
import PortalLoader from '../components/client-portal/PortalLoader';
import PixieWelcome from '../components/client-portal/PixieWelcome';
import NewClientBanner from '../components/client-portal/NewClientBanner';
import ContactSection from '../components/client-portal/ContactSection';
import GamesZoneCard from '../components/client-portal/GamesZoneCard';
import LoginCard from '../components/client-portal/LoginCard';
import FooterSection from '../components/client-portal/FooterSection';
import FamilyMessage from '../components/client-portal/FamilyMessage';
import { SpaceParticles, GlowCircles } from '../components/client-portal/SpaceEffects';
// Note: DeleteAccountModal est réservé au module Heartbeats uniquement
import SpaceCabinProfile from '../components/client-portal/SpaceCabinProfile';
// 🚀 LAUNCHPAD - Navigation style macOS
import QuickAccessGrid from '../components/client-portal/QuickAccessGrid';
// 🎂 ANNIVERSAIRE 1 AN - Flyer Concours
import AnniversaryFlyer from '../components/landing/AnniversaryFlyer';

export default function ClientPortalPage() {
  // 🔐 HOOK D'AUTHENTIFICATION - Gère toute la logique de connexion
  const [heartbeatsProfile, setHeartbeatsProfile] = useState(null);
  
  const {
    // États auth de base
    phone, setPhone,
    hasStoredPhone,
    setHasStoredPhone,
    pin, setPin,
    isLoggedIn, setIsLoggedIn,
    customer, setCustomer,
    cards, setCards,
    isLoading,
    isCheckingSession,
    // États 2FA
    show2FAVerification, setShow2FAVerification,
    pending2FACustomer, setPending2FACustomer,
    totpStatus, setTotpStatus,
    // États Email
    loginMethod, setLoginMethod,
    email, setEmail,
    emailCode, setEmailCode,
    emailCodeSent,
    emailCooldown,
    // Refs
    pinRefs,
    emailCodeRefs,
    // Handlers
    handleLogout,
    handlePinInput,
    handleKeyDown,
    handleLogin,
    handleSendEmailCode,
    handleEmailCodeInput,
    handleVerifyEmailCode,
    resetEmailForm,
    handle2FASuccess,
  } = useClientAuth({
    onLoginSuccess: async (customerData) => {
      // Utiliser completeLogin pour tout gérer (sons, confettis, QR, etc.)
      await completeLogin(customerData);
    },
    onHeartbeatsProfileLoaded: (profile) => {
      setHeartbeatsProfile(profile);
    }
  });
  
  // Charger les préférences d'accessibilité
  const getLargeTextMode = () => {
    try {
      return localStorage.getItem('largeTextMode') === 'true';
    } catch {
      return false;
    }
  };
  
  // États locaux (pas dans le hook)
  const [messages, setMessages] = useState([]);
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [showParentalConsent, setShowParentalConsent] = useState(false);
  const [largeTextMode, setLargeTextMode] = useState(getLargeTextMode());
  // 2FA Setup (différent de verification)
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FAGuide, setShow2FAGuide] = useState(false);
  const [debtInfo, setDebtInfo] = useState(null); // Info dette du client
  const [showGames, setShowGames] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false); // Modal livraison
  const [showMaxAssistant, setShowMaxAssistant] = useState(false); // 🛵 Max Assistant Livraison
  const [showAppsHub, setShowAppsHub] = useState(false); // Pour tracker la vue Apps Hub
  const [showHeartbeats, setShowHeartbeats] = useState(false); // 🔥 Heartbeats - Rencontres
  const [heartbeatsTripleTap, setHeartbeatsTripleTap] = useState(0); // 👻 Triple-tap pour mode confidentiel
  const [newMessage, setNewMessage] = useState(''); // Message pour le chat
  const [chatImage, setChatImage] = useState(null); // 📸 Image pour le chat "Parle-nous"
  const [chatImagePreview, setChatImagePreview] = useState(null); // Prévisualisation de l'image
  const chatFileInputRef = useRef(null); // Référence pour l'input file
  // 💳 Stripe Payment States
  const [showStripePayment, setShowStripePayment] = useState(false);
  // 📊 Stats accordéon
  const [showStats, setShowStats] = useState(false);
  // 🚀 SAS PASSAGER - Afficher le sas d'entrée avant la connexion
  const [showSas, setShowSas] = useState(true);
  // 🔄 Vérification session locale (complète celle du hook)
  const [isCheckingSessionLocal, setIsCheckingSessionLocal] = useState(true);
  
  // 🚀 PIXIE SPATIALE - Messages d'accueil pour la page de connexion
  const PIXIE_SPACE_MESSAGES = [
    "Commandante Pixie à bord ! 🚀 Bienvenue dans ton espace VIP...",
    "Hey toi ! 😏 Prêt(e) pour un voyage vers tes avantages exclusifs ?",
    "Station orbitale LPE... Connexion sécurisée en cours ! ✨",
    "Les étoiles t'attendaient... Entre ton code d'accès ! 🌟",
    "Bienvenue à bord, passager(e) privilégié(e) ! 💜",
  ];
  const [pixieSpaceMessage] = useState(() => 
    PIXIE_SPACE_MESSAGES[Math.floor(Math.random() * PIXIE_SPACE_MESSAGES.length)]
  );
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Hook pour le thème Jour/Nuit
  const { isDarkMode } = useTheme();

  // Analytics tracking
  const { trackPageView, trackAction } = useAnalytics(customer?.id, isLoggedIn);

  // 🎉 HOOKS POUR LES EFFETS SONORES ET CONFETTIS
  // Détection de nouveaux badges (joue son + confettis automatiquement)
  const { badges } = useBadgeDetection(customer, cards, (badge) => {
    console.log('🏅 Nouveau badge débloqué:', badge.name);
    // Le son et les confettis sont joués automatiquement par le hook
    toast.success(`🏅 Badge débloqué : ${badge.name} !`, {
      description: badge.description,
      duration: 5000,
    });
  });

  // Son à l'obtention d'un tampon
  const { totalStamps } = useStampSound(cards);

  // 🧲 Hook magnétique pour Heartbeats (heartbeatsProfile est géré par useClientAuth)
  const magneticHook = useMagneticHook(
    customer?.id,
    !!heartbeatsProfile,  // hasProfile
    heartbeatsProfile?.completionPercent || 0  // profileCompletion
  );

  // Son de match Heartbeats
  const { playMatchSound } = useMatchSound();

  // Focus sur le PIN si le numéro est déjà mémorisé
  useEffect(() => {
    if (hasStoredPhone && phone && !isLoggedIn && loginMethod === 'phone') {
      // Focus sur le premier champ PIN après un court délai
      setTimeout(() => {
        document.getElementById('client-pin-0')?.focus();
      }, 300);
    }
  }, [hasStoredPhone, phone, isLoggedIn, loginMethod]);

  // Tracker les changements de vue
  useEffect(() => {
    if (!isLoggedIn || !customer?.id) return;
    
    if (showGames) {
      trackPageView('games_hub');
    } else if (showChat) {
      trackPageView('chat');
    } else if (showAppsHub) {
      trackPageView('apps_hub');
    } else if (showProfileEditor) {
      trackPageView('profile_editor');
    } else if (showDeliveryModal) {
      trackPageView('delivery_order');
    } else {
      trackPageView('dashboard');
    }
  }, [isLoggedIn, customer?.id, showGames, showChat, showAppsHub, showProfileEditor, showDeliveryModal, trackPageView]);

  // Vérifier si l'utilisateur revient d'une connexion Google
  useEffect(() => {
    const checkExistingSession = async () => {
      // Check if coming from Google Auth with user data
      if (location.state?.fromGoogleAuth && location.state?.user) {
        const userData = location.state.user;
        setCustomer(userData);
        setIsLoggedIn(true);
        loadCustomerData(userData);
        setIsCheckingSessionLocal(false);
        return;
      }

      // Check for existing session token
      const sessionToken = localStorage.getItem('clientSessionToken');
      if (sessionToken) {
        try {
          const userData = await api.getAuthMe(sessionToken);
          if (userData) {
            setCustomer(userData);
            setIsLoggedIn(true);
            loadCustomerData(userData);
          }
        } catch (error) {
          // Session expired or invalid
          localStorage.removeItem('clientSessionToken');
          localStorage.removeItem('clientCustomer');
        }
      }
      setIsCheckingSessionLocal(false);
    };

    checkExistingSession();
  }, [location]);

  // Gestion du cooldown email (géré par le hook mais on garde l'effet local pour la UI)
  useEffect(() => {
    if (emailCooldown > 0) {
      const timer = setTimeout(() => {}, 1000);
      return () => clearTimeout(timer);
    }
  }, [emailCooldown]);

  // 🚚 DÉTECTION DU PARAMÈTRE action=livraison POUR OUVRIR LE MODULE DE LIVRAISON
  useEffect(() => {
    if (isLoggedIn && customer) {
      const searchParams = new URLSearchParams(location.search);
      const action = searchParams.get('action');
      
      if (action === 'livraison') {
        // Ouvrir le modal de livraison automatiquement !
        setShowDeliveryModal(true);
        toast.success('🚚 Module de livraison ouvert !', {
          description: `Bienvenue ${extractFirstName(customer.name)} ! Passez votre commande.`,
        });
        // Nettoyer l'URL
        navigate('/client', { replace: true });
      } else if (action === 'heartbeats') {
        // Ouvrir Heartbeats automatiquement
        setShowHeartbeats(true);
        navigate('/client', { replace: true });
      }
    }
  }, [isLoggedIn, customer, location.search, navigate]);

  // 🔐 Générer le QR Code quand le customer est chargé (pour les sessions existantes)
  useEffect(() => {
    const generateQRCode = async () => {
      if (customer?.qrCode && !qrCodeImage) {
        try {
          const qrImage = await QRCodeCanvas.toDataURL(customer.qrCode, {
            width: 300,
            margin: 2,
            color: {
              dark: '#00FFFF',
              light: '#0a0015'
            }
          });
          setQrCodeImage(qrImage);
          console.log('🔐 QR Code généré pour session existante');
        } catch (err) {
          console.error('Erreur génération QR Code:', err);
        }
      }
    };
    generateQRCode();
  }, [customer?.qrCode, qrCodeImage]);

  const completeLogin = async (customerData) => {
    setCustomer(customerData);
    await loadCustomerData(customerData.id);
    setIsLoggedIn(true);
    
    // 🔇 Arrêter les sons précédents (sas, warp, etc.) pour éviter les chevauchements
    soundSystem.stopAllSounds();
    
    // 🎊 SURPRISE ! Son et confettis (une seule fois, après arrêt des sons précédents)
    setTimeout(() => {
      // 🎉 Confettis variés
      const confettiStyles = [
        () => welcomeConfetti(),
        () => victoryConfetti(),
        () => matchConfetti(),
      ];
      const randomConfetti = confettiStyles[Math.floor(Math.random() * confettiStyles.length)];
      randomConfetti();
      
      // 🔊 UN SEUL son aléatoire
      const soundPlayed = soundSystem.playRandomWelcome();
      console.log('🎮 Son surprise:', soundPlayed);
    }, 800); // Délai augmenté pour laisser le temps aux sons précédents de se terminer
    
    // Messages de bienvenue variés
    const welcomeMessages = [
      `Bienvenue ${customerData.name || customerData.firstName} ! 🚀`,
      `Hey ${customerData.name || customerData.firstName} ! Prêt(e) pour l'aventure ? 💜`,
      `${customerData.name || customerData.firstName} est dans la place ! 🔥`,
      `Commandante Pixie salue ${customerData.name || customerData.firstName} ! 🧚`,
      `${customerData.name || customerData.firstName}, ton équipage t'attendait ! ⭐`,
      `Waouh ${customerData.name || customerData.firstName} ! Content de te revoir ! 🎉`,
    ];
    const randomMessage = welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];
    toast.success(randomMessage);
    
    // Charger le statut 2FA
    try {
      const status = await api.getTOTPStatus(customerData.id);
      setTotpStatus(status);
    } catch (e) {
      setTotpStatus({ enabled: false });
    }
    
    // Generate QR code image
    if (customerData.qrCode) {
      const qrImage = await QRCodeCanvas.toDataURL(customerData.qrCode, {
        width: 300,
        margin: 2,
        color: {
          dark: '#00FFFF',
          light: '#0a0015'
        }
      });
      setQrCodeImage(qrImage);
    }
    
    // Charger le profil Heartbeats
    try {
      const hbProfile = await api.getHeartbeatsProfile(customerData.id);
      if (hbProfile) {
        setHeartbeatsProfile(hbProfile);
      }
    } catch (e) {
      console.log('Pas de profil Heartbeats');
    }
  };

  // 🔐 Handler 2FA local (utilise completeLogin qui est spécifique à cette page)
  const handleLocal2FASuccess = async (customerData) => {
    setShow2FAVerification(false);
    setPending2FACustomer(null);
    await completeLogin(customerData);
  };

  const loadCustomerData = async (customerId) => {
    try {
      const [cardsData, messagesData] = await Promise.all([
        api.getCustomerCards(customerId),
        api.getCustomerMessages(customerId)
      ]);
      console.log('🔐 Cartes chargées:', cardsData?.length || 0, 'cartes', cardsData);
      setCards(cardsData || []);
      setMessages(messagesData);
      
      // 💜 Charger le profil Heartbeats
      try {
        const hbProfile = await api.getHeartbeatsProfile(customerId);
        if (hbProfile && hbProfile.customerId) {
          setHeartbeatsProfile(hbProfile);
        }
      } catch (e) {
        // Pas de profil Heartbeats = normal pour les nouveaux clients
        setHeartbeatsProfile(null);
      }
      
      // Vérifier si le client a une dette - d'abord par ID, puis par téléphone
      try {
        // console.log('[DEBT] Checking debt for customer:', customerId);
        let debt = await api.checkCustomerDebt(customerId);
        // console.log('[DEBT] Result by ID:', debt);
        
        // Si pas de dette trouvée par ID, essayer par téléphone
        if (!debt.hasDebt && customer?.phone) {
          // console.log('[DEBT] Trying by phone:', customer.phone);
          debt = await api.checkCustomerDebtByPhone(customer.phone);
          // console.log('[DEBT] Result by phone:', debt);
        }
        
        setDebtInfo(debt);
      } catch (e) {
        console.error('[DEBT] Error:', e);
        setDebtInfo({ hasDebt: false, balance: 0 });
      }
    } catch (error) {
      console.error('Erreur chargement données');
    }
  };

  const handleShareReferral = () => {
    if (navigator.share && customer?.referralCode) {
      navigator.share({
        title: 'La Petite Épicerie - Parrainage',
        text: `Rejoins La Petite Épicerie avec mon code parrain : ${customer.referralCode}`,
        url: window.location.origin
      }).catch(() => {});
    } else if (customer?.referralCode) {
      navigator.clipboard.writeText(customer.referralCode);
      toast.success('Code parrain copié !');
    }
  };

  const badgeNames = {
    newcomer: 'Petit Nouveau',
    bronze: 'Bronze',
    silver: 'Argent',
    gold: 'Or',
    platinum: 'Platine',
    explorer: 'Explorateur',
    marathon: 'Marathonien',
    influencer: 'Influenceur'
  };

  const badgeIcons = {
    newcomer: '🚀',
    bronze: '🥉',
    silver: '🥈',
    gold: '🥇',
    platinum: '💎',
    explorer: '🧭',
    marathon: '🏃',
    influencer: '📢'
  };

  // Afficher un écran de chargement pendant la vérification de session
  if (isCheckingSession || isCheckingSessionLocal) {
    return <PortalLoader />;
  }

  // 🚀 SAS PASSAGER - Afficher avant la connexion
  if (!isLoggedIn && showSas) {
    return (
      <SasPassager 
        onEnterClientPortal={() => setShowSas(false)}
        onEnterHeartbeats={() => {
          setShowSas(false);
          // Après connexion, on affichera Heartbeats
        }}
      />
    );
  }

  if (!isLoggedIn) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 overflow-x-hidden relative"
        style={{
          background: 'linear-gradient(135deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)',
        }}
      >
        {/* 🌌 PARTICULES SPATIALES FLOTTANTES */}
        <SpaceParticles count={40} />

        {/* 🔮 CERCLES LUMINEUX DE FOND */}
        <GlowCircles />

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="w-full max-w-lg z-10"
        >
          {/* Bouton Retour */}
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="mb-4 text-cyan-400 hover:text-cyan-300"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour
          </Button>

          {/* 🧚 PIXIE SPATIALE - DÉSACTIVÉ TEMPORAIREMENT */}
          {/* <PixieWelcome message={pixieSpaceMessage} /> */}

          {/* Titre Espace Client-Malin */}
          <motion.div 
            className="text-center mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1" style={{ textShadow: '0 0 30px rgba(255, 0, 255, 0.6)' }}>
              🚀 Espace Client-Malin
            </h1>
            <p className="text-purple-300 text-sm">La Petite Épicerie • Accès VIP</p>
          </motion.div>

          {/* 🔮 CARTE DE CONNEXION SPATIALE */}
          <LoginCard
            loginMethod={loginMethod}
            setLoginMethod={setLoginMethod}
            phone={phone}
            setPhone={setPhone}
            hasStoredPhone={hasStoredPhone}
            setHasStoredPhone={setHasStoredPhone}
            pin={pin}
            pinRefs={pinRefs}
            handlePinInput={handlePinInput}
            handleKeyDown={handleKeyDown}
            handleLogin={handleLogin}
            email={email}
            setEmail={setEmail}
            emailCode={emailCode}
            emailCodeRefs={emailCodeRefs}
            emailCodeSent={emailCodeSent}
            emailCooldown={emailCooldown}
            handleSendEmailCode={handleSendEmailCode}
            handleEmailCodeInput={handleEmailCodeInput}
            handleVerifyEmailCode={handleVerifyEmailCode}
            resetEmailForm={resetEmailForm}
            isLoading={isLoading}
          />
          
          {/* 🎁 Bandeau Nouveau Client - Style Spatial */}
          <NewClientBanner />
        </motion.div>

        {/* 2FA Verification Modal (dans le bloc login) */}
        {show2FAVerification && pending2FACustomer && (
          <TOTPLoginVerification
            customerId={pending2FACustomer.id}
            customerName={pending2FACustomer.name}
            onSuccess={handleLocal2FASuccess}
            onCancel={() => {
              setShow2FAVerification(false);
              setPending2FACustomer(null);
              setPin(['', '', '', '']);
            }}
          />
        )}
      </div>
    );
  }

  const getCardProgress = (card) => {
    return card.boxes.filter(b => b).length;
  };

  const getTotalBoxes = (card) => {
    return card.type === 'puffs' ? 40 : 10;
  };

  return (
    <div 
      className={`p-4 sm:p-6 overflow-x-hidden overflow-y-auto transition-colors duration-500 ${largeTextMode ? 'large-text-mode' : ''}`}
      style={{
        // ✅ FIX iOS: Utiliser dvh avec fallback
        minHeight: 'var(--app-height, 100vh)',
        height: 'auto',
        background: isDarkMode 
          ? 'linear-gradient(135deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)'
          : 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 50%, #f5f7ff 100%)',
        // ✅ FIX iOS: Safe area padding
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))',
        // 🔧 FIX SAMSUNG Z FLIP: Forcer le scroll tactile
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
        // Fix pour téléphones pliables
        position: 'relative',
      }}
    >
      <InstallPWAPrompt />
      
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex justify-between items-center">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className={isDarkMode ? "text-cyan-400" : "text-indigo-600"}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Accueil
          </Button>
          <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Mon Espace Client-Malin</h1>
          <div className="flex items-center gap-2">
            <NotificationToggle customerId={customer?.id} />
            <Button
              variant="ghost"
              onClick={() => {
                // Fermer tous les modals/chats avant déconnexion
                setShowChat(false);
                setShowMaxAssistant(false);
                setShowProfileEditor(false);
                setShowGames(false);
                setShowAppsHub(false);
                // Appeler la vraie déconnexion
                handleLogout();
              }}
              className="text-red-400"
              size="sm"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      {/* MESSAGE D'ACCUEIL PERSONNALISÉ - MACHINE À POSITIVER ! */}
      <WelcomeMessage customer={customer} isDarkMode={isDarkMode} />

      {/* 🎫 CARTE D'EMBARCATION SPATIALE - Pass VIP du Vaisseau */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="max-w-4xl mx-auto mb-6 qrcode-section"
        data-section="qrcode"
        style={{ touchAction: 'pan-y' }}
      >
        <div 
          className="relative overflow-hidden rounded-3xl"
          style={{
            background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)',
            boxShadow: '0 0 60px rgba(6, 182, 212, 0.3), 0 25px 50px rgba(0,0,0,0.5)',
            border: '1px solid rgba(6, 182, 212, 0.3)'
          }}
        >
          {/* 🌠 Étoiles animées en fond */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(25)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 rounded-full bg-cyan-400"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  opacity: Math.random() * 0.4 + 0.1
                }}
                animate={{
                  opacity: [0.1, 0.6, 0.1],
                  scale: [1, 1.5, 1]
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2
                }}
              />
            ))}
          </div>

          {/* 🔷 Bordures HUD */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
          
          {/* Corner HUD */}
          <div className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-cyan-500/50" />
          <div className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-cyan-500/50" />
          <div className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-cyan-500/30" />
          <div className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-cyan-500/30" />

          {/* 🎛️ Header */}
          <div className="relative px-6 py-4 border-b border-cyan-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <motion.div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(6, 182, 212, 0.1) 100%)',
                    border: '1px solid rgba(6, 182, 212, 0.3)'
                  }}
                  animate={{
                    boxShadow: ['0 0 10px rgba(6, 182, 212, 0.2)', '0 0 20px rgba(6, 182, 212, 0.4)', '0 0 10px rgba(6, 182, 212, 0.2)']
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <span className="text-xl">🎫</span>
                </motion.div>
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    Carte d'Embarcation
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                      VIP PASS
                    </span>
                  </h3>
                  <p className="text-[11px] text-cyan-500/70 font-mono tracking-wider">
                    STATION LPE • ACCÈS PRIORITAIRE
                  </p>
                </div>
              </div>
              
              {/* Status indicator */}
              <div className="flex items-center gap-2">
                <motion.div
                  className="w-2 h-2 rounded-full bg-green-500"
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
                <span className="text-[10px] font-mono text-green-400">VALIDÉ</span>
              </div>
            </div>
          </div>

          {/* 📱 QR Code Zone */}
          <div className="relative px-6 py-8">
            {qrCodeImage ? (
              <div className="flex flex-col md:flex-row items-center gap-8">
                {/* QR Code avec effets */}
                <motion.div 
                  className="relative flex-shrink-0"
                  whileHover={{ scale: 1.02 }}
                >
                  {/* Orbite extérieure */}
                  <motion.div
                    className="absolute -inset-4 rounded-2xl border border-dashed border-cyan-500/30"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  />
                  
                  {/* Conteneur QR */}
                  <div 
                    className="relative p-5 rounded-2xl"
                    style={{
                      background: 'rgba(2, 6, 23, 0.9)',
                      border: '3px solid #06b6d4',
                      boxShadow: '0 0 40px rgba(6, 182, 212, 0.4), inset 0 0 20px rgba(6, 182, 212, 0.1)'
                    }}
                  >
                    {/* Scan line effect */}
                    <motion.div
                      className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none"
                      style={{ zIndex: 1 }}
                    >
                      <motion.div
                        className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent"
                        animate={{ top: ['0%', '100%', '0%'] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                      />
                    </motion.div>
                    
                    <img 
                      src={qrCodeImage} 
                      alt="Carte d'Embarcation" 
                      className="w-48 h-48 md:w-52 md:h-52 relative z-0"
                    />
                    
                    {/* Corner accents */}
                    <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-cyan-400" />
                    <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-cyan-400" />
                    <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-cyan-400" />
                    <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-cyan-400" />
                  </div>
                </motion.div>

                {/* Infos du passager */}
                <div className="flex-1 text-center md:text-left space-y-4">
                  {/* Code d'accès */}
                  <div>
                    <p className="text-[10px] font-mono text-cyan-500/60 tracking-[0.3em] mb-1">CODE D'ACCÈS</p>
                    <motion.div 
                      className="font-mono text-2xl md:text-3xl font-bold text-cyan-400 tracking-widest"
                      style={{ textShadow: '0 0 20px rgba(6, 182, 212, 0.5)' }}
                      animate={{ opacity: [1, 0.8, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {customer.qrCode}
                    </motion.div>
                  </div>
                  
                  {/* Passager */}
                  <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-[10px] font-mono text-gray-500 mb-1">PASSAGER</p>
                    <p className="text-white font-semibold">{customer.name}</p>
                  </div>
                  
                  {/* Instructions */}
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/20">
                    <span className="text-lg">👨‍✈️</span>
                    <div>
                      <p className="text-sm text-cyan-300 font-medium">Présentez ce pass au commandant de bord</p>
                      <p className="text-xs text-gray-500 mt-1">Scan ou saisie manuelle du code acceptés</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-12 text-center">
                <motion.div
                  className="w-16 h-16 mx-auto mb-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center"
                  animate={{ 
                    boxShadow: ['0 0 10px rgba(6, 182, 212, 0.2)', '0 0 30px rgba(6, 182, 212, 0.4)', '0 0 10px rgba(6, 182, 212, 0.2)']
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <motion.span
                    className="text-2xl"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    🛸
                  </motion.span>
                </motion.div>
                <p className="text-cyan-400 font-mono text-sm">Génération du pass en cours...</p>
                <p className="text-gray-600 text-xs mt-1">Synchronisation avec la station orbitale</p>
              </div>
            )}
          </div>

          {/* 🔻 Footer HUD */}
          <div className="px-6 py-3 border-t border-cyan-500/10 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] font-mono text-gray-600">
              <span>🚀</span>
              <span>LA PETITE ÉPICERIE • BOARDING PASS v2.0</span>
            </div>
            <div className="flex items-center gap-1">
              <motion.div 
                className="w-1.5 h-1.5 rounded-full bg-cyan-500"
                animate={{ opacity: [1, 0.5, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400/50" />
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-300/30" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* 🚀 ACCÈS RAPIDE - Grille d'icônes style macOS */}
      <QuickAccessGrid
        onAction={(action, target) => {
          if (action === 'setShow') {
            switch(target) {
              case 'showGames': setShowGames(true); break;
              case 'showHeartbeats': setShowHeartbeats(true); break;
              case 'showMaxAssistant': setShowMaxAssistant(true); break;
              case 'showProfileEditor': setShowProfileEditor(true); break;
              case 'showStats': setShowStats(true); break;
              case 'show2FASetup': setShow2FASetup(true); break;
              default: break;
            }
          } else if (action === 'scrollAndOpen') {
            // Pour les stats : ouvrir l'accordéon puis scroller
            if (target === 'stats') {
              setShowStats(true);
              setTimeout(() => {
                const statsEl = document.querySelector('.stats-section, [data-section="stats"]');
                if (statsEl) {
                  statsEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 100);
            }
          }
        }}
      />

      {/* 🎂 FLYER ANNIVERSAIRE 1 AN - Grand Jeu Concours */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="max-w-4xl mx-auto mb-6"
      >
        <AnniversaryFlyer variant="card" />
      </motion.div>

      {/* 🚀 SPACE CABIN PROFILE - Cabine du Vaisseau */}
      <SpaceCabinProfile
        customer={customer}
        debtInfo={debtInfo}
        totpStatus={totpStatus}
        largeTextMode={largeTextMode}
        setLargeTextMode={setLargeTextMode}
        setShowProfileEditor={setShowProfileEditor}
        setShow2FASetup={setShow2FASetup}
        setShow2FAGuide={setShow2FAGuide}
        setShowHeartbeats={setShowHeartbeats}
        heartbeatsTripleTap={heartbeatsTripleTap}
        setHeartbeatsTripleTap={setHeartbeatsTripleTap}
        isDarkMode={isDarkMode}
        badgeIcons={badgeIcons}
        badgeNames={badgeNames}
      />


      {/* AUTORISATION PARENTALE (si mineur) */}
      {customer.isMinor && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.07 }}
          className="max-w-4xl mx-auto mb-6"
        >
          <div 
            className="backdrop-blur-xl rounded-2xl p-5 border-2"
            style={{
              background: customer.parentalConsentSigned 
                ? 'rgba(34, 197, 94, 0.1)' 
                : 'rgba(234, 179, 8, 0.1)',
              borderColor: customer.parentalConsentSigned ? '#22C55E' : '#EAB308',
              boxShadow: customer.parentalConsentSigned 
                ? '0 0 20px rgba(34, 197, 94, 0.3)' 
                : '0 0 20px rgba(234, 179, 8, 0.3)'
            }}
          >
            {customer.parentalConsentSigned ? (
              <div>
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <FileCheck className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-green-400">
                      ✅ Autorisation Parentale Signée
                    </h3>
                    <p className="text-gray-400 text-sm">
                      Par {customer.parentalConsentSignerName} le {new Date(customer.parentalConsentDate).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                {/* Cartes autorisées */}
                {customer.parentalConsentCards && customer.parentalConsentCards.length > 0 && (
                  <div className="mb-3 p-3 rounded-lg bg-black/20">
                    <p className="text-xs text-gray-400 mb-2">Cartes autorisées :</p>
                    <div className="flex flex-wrap gap-2">
                      {customer.parentalConsentCards.includes('alcohol') && (
                        <span className="px-2 py-1 rounded bg-red-900/50 text-red-300 text-xs">🍷 Alcool</span>
                      )}
                      {customer.parentalConsentCards.includes('cbd') && (
                        <span className="px-2 py-1 rounded bg-green-900/50 text-green-300 text-xs">🌿 CBD</span>
                      )}
                      {customer.parentalConsentCards.includes('puffs') && (
                        <span className="px-2 py-1 rounded bg-blue-900/50 text-blue-300 text-xs">💨 Puffs</span>
                      )}
                    </div>
                  </div>
                )}
                <DownloadConsentButton customer={customer} />
              </div>
            ) : (
              <div>
                <div className="flex items-start gap-4 mb-4">
                  <AlertTriangle className="w-8 h-8 text-yellow-500 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-yellow-400 mb-1">
                      Autorisation Parentale Requise
                    </h3>
                    <p className="text-gray-400 text-sm">
                      En tant que mineur, vous devez obtenir la signature de votre responsable légal 
                      pour pouvoir bénéficier du programme de fidélité sur les produits réglementés.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowParentalConsent(true)}
                  className="w-full font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #EAB308 0%, #F59E0B 100%)',
                    color: '#000'
                  }}
                >
                  <FileCheck className="w-5 h-5 mr-2" />
                  Faire signer l&apos;autorisation parentale
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* 🎯 BLOC PARRAINAGE - Juste après Membre Privilégié */}
      {customer.referralCode && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.07 }}
          className="max-w-4xl mx-auto mb-6 referral-section"
          data-section="referral"
        >
          <ReferralShowcase customer={customer} onUpdate={setCustomer} />
        </motion.div>
      )}

      {/* Section CONTACT - Appeler, Snap, Livraison + Chat intégré */}
      <div data-section="contact" className="contact-section">
        <ContactSection 
          isDarkMode={isDarkMode} 
          onDeliveryClick={() => setShowMaxAssistant(true)}
        >
        {/* 💬 Chat intégré dans "On est là pour toi" */}
        <div className="mt-5 pt-5 border-t border-white/10">
          {/* Header Chat */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'
              }`}>
                <MessageSquare className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  💬 Parle-nous !
                </h4>
                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Réponse sous 24h
                </p>
              </div>
            </div>
            {messages.filter(m => !m.read && m.fromAdmin).length > 0 && (
              <span className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold animate-pulse">
                {messages.filter(m => !m.read && m.fromAdmin).length} nouveau(x)
              </span>
            )}
          </div>
          
          {/* Zone de messages */}
          <div 
            className="space-y-3 max-h-64 overflow-y-auto mb-4 p-3 rounded-xl"
            style={{ background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)' }}
            id="chat-messages"
          >
            {messages.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Aucun message</p>
                <p className="text-xs">Envoyez-nous un message ! 👋</p>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div 
                  key={msg.id || idx}
                  className={`flex ${msg.fromAdmin ? 'justify-start' : 'justify-end'}`}
                >
                  <div 
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.fromAdmin 
                        ? 'bg-amber-500/15 border border-amber-500/20 rounded-tl-none' 
                        : 'bg-indigo-500/15 border border-indigo-500/20 rounded-tr-none'
                    }`}
                    onClick={async () => {
                      if (!msg.read && msg.fromAdmin && msg.id) {
                        await api.markMessageRead(msg.id);
                        setMessages(messages.map(m => m.id === msg.id ? {...m, read: true} : m));
                      }
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {msg.fromAdmin ? (
                        <span className="text-xs font-bold text-amber-400">🏪 L'équipe</span>
                      ) : (
                        <span className="text-xs font-bold text-indigo-400">Vous</span>
                      )}
                    </div>
                    {msg.imageBase64 && (
                      <div className="mb-2">
                        <img 
                          src={msg.imageBase64} 
                          alt="Photo" 
                          className="max-w-full rounded-lg border border-white/10 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(msg.imageBase64, '_blank');
                          }}
                        />
                      </div>
                    )}
                    {msg.content && (
                      <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{msg.content}</p>
                    )}
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-[10px] text-gray-500">
                        {msg.timestamp && !isNaN(new Date(msg.timestamp).getTime()) 
                          ? new Date(msg.timestamp).toLocaleString('fr-FR', { 
                              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                            })
                          : msg.createdAt && !isNaN(new Date(msg.createdAt).getTime())
                            ? new Date(msg.createdAt).toLocaleString('fr-FR', { 
                                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                              })
                            : 'À l\'instant'
                        }
                      </span>
                      {!msg.fromAdmin && (
                        <span className={`text-[10px] ${msg.read ? 'text-green-400' : 'text-gray-500'}`}>
                          {msg.read ? '✓✓' : '✓'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Prévisualisation image */}
          {chatImagePreview && (
            <div className={`mb-3 p-3 rounded-xl ${isDarkMode ? 'bg-black/30' : 'bg-gray-100'} border border-amber-500/20`}>
              <div className="flex items-center gap-3">
                <img src={chatImagePreview} alt="Aperçu" className="w-14 h-14 object-cover rounded-lg border border-amber-500/30" />
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>📸 Photo prête</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setChatImage(null); setChatImagePreview(null); if (chatFileInputRef.current) chatFileInputRef.current.value = ''; }}
                  className="p-2 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          
          {/* Formulaire d'envoi */}
          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              if (!newMessage.trim() && !chatImage) return;
              try {
                if (chatImage) {
                  await api.sendChatMessage({
                    customerId: customer.id,
                    customerName: customer.name || customer.firstName || 'Client',
                    message: newMessage,
                    channel: 'team',
                    source: 'client_portal',
                    imageBase64: chatImage
                  });
                } else {
                  await api.clientSendMessage(customer.id, newMessage);
                }
                setMessages([...messages, {
                  id: Date.now().toString(),
                  content: newMessage,
                  imageBase64: chatImage,
                  fromAdmin: false,
                  timestamp: new Date().toISOString(),
                  read: false
                }]);
                setNewMessage('');
                setChatImage(null);
                setChatImagePreview(null);
                if (chatFileInputRef.current) chatFileInputRef.current.value = '';
                toast.success(chatImage ? 'Photo envoyée ! 📸' : 'Message envoyé ! 📨');
                setTimeout(() => {
                  const chatDiv = document.getElementById('chat-messages');
                  if (chatDiv) chatDiv.scrollTop = chatDiv.scrollHeight;
                }, 100);
              } catch (error) {
                toast.error('Erreur d\'envoi');
              }
            }}
            className="flex gap-2 items-center"
          >
            <input ref={chatFileInputRef} type="file" accept="image/*" onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (!file.type.startsWith('image/')) { toast.error('Images uniquement 📸'); return; }
              if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
              const reader = new FileReader();
              reader.onload = (event) => {
                const img = new window.Image();
                img.onload = () => {
                  const canvas = document.createElement('canvas');
                  const maxSize = 600;
                  let width = img.width, height = img.height;
                  if (width > height && width > maxSize) { height = (height * maxSize) / width; width = maxSize; }
                  else if (height > maxSize) { width = (width * maxSize) / height; height = maxSize; }
                  canvas.width = width; canvas.height = height;
                  canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                  const compressed = canvas.toDataURL('image/jpeg', 0.7);
                  setChatImage(compressed); setChatImagePreview(compressed);
                };
                img.src = event.target.result;
              };
              reader.readAsDataURL(file);
            }} className="hidden" />
            
            <button
              type="button"
              onClick={() => chatFileInputRef.current?.click()}
              className={`p-3 rounded-xl transition-all ${isDarkMode ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-400' : 'bg-amber-100 hover:bg-amber-200 text-amber-600'} ${chatImage ? 'ring-2 ring-amber-500' : ''}`}
            >
              <ImagePlus className="w-5 h-5" />
            </button>
            
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={chatImage ? "Commentaire..." : "Ton message..."}
              className={`flex-1 px-4 py-3 rounded-xl ${isDarkMode ? 'bg-black/20 text-white placeholder-gray-500 border border-white/10 focus:border-indigo-500' : 'bg-gray-100 text-gray-900 placeholder-gray-500 border border-gray-200 focus:border-indigo-500'} outline-none transition-colors`}
            />
            
            <Button
              type="submit"
              disabled={!newMessage.trim() && !chatImage}
              className="px-5 py-3 rounded-xl font-bold disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
            >
              <Send className="w-5 h-5 text-white" />
            </Button>
          </form>
        </div>
      </ContactSection>
      </div>

      {/* 📰 ACTUALITÉS LPE - Après Chat */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.13 }}
        className="max-w-4xl mx-auto mb-6 news-section"
        data-section="news"
      >
        <NewsFeed customerId={customer?.id} />
      </motion.div>

      {/* ⚡ OFFRES FLASH - Après Actualités */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.14 }}
        className="max-w-4xl mx-auto mb-6 promos-section"
        data-section="promos"
      >
        <FlashOffers customerId={customer?.id} />
      </motion.div>

      {/* 📊 SONDAGES - Après Offres Flash */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="max-w-4xl mx-auto mb-6 polls-section"
        data-section="polls"
      >
        <PollsWidget customerId={customer?.id} />
      </motion.div>

      {/* 🎉 COOL FEATURES SECTION */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.18 }}
        className="max-w-4xl mx-auto mb-6 detente-section"
        data-section="detente"
      >
        <h3 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          ☕ Ta pause détente
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
          {/* Radio */}
          <div className="h-full">
            <RadioPlayer />
          </div>
          
          {/* Météo */}
          <div className="h-full">
            <WeatherWidget />
          </div>
        </div>

        {/* 🎮 JOUER & GAGNER - Dans Espace Détente */}
        <motion.div 
          className="mt-4"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.22 }}
        >
          {/* 🎮 BLOC JEUX SPECTACULAIRE - Comme Heartbeats ! */}
          <GamesZoneCard isDarkMode={isDarkMode} onOpenGames={() => setShowGames(true)} />
        </motion.div>
        
        {/* Calendrier des événements - APRÈS Radio/Météo */}
        <div className="mt-4 events-section" data-section="events">
          <EventsCalendar customerId={customer?.id} />
        </div>

        {/* TV & Streaming (inclut maintenant IPTV) */}
        <div className="mt-4 tv-section" data-section="tv">
          <TVPlayer />
        </div>

        {/* Apps Hub - Accès rapide aux applications populaires */}
        <div className="mt-4 apps-section" data-section="apps">
          <AppsHub />
        </div>
      </motion.div>

      {/* Birthday Gift Component (flottant) */}
      <BirthdayGift 
        customer={customer} 
        onGiftClaimed={() => {
          // Recharger le client
          api.getCustomerByPhone(phone).then(c => setCustomer(c)).catch(() => {});
        }}
      />

      {/* Cards */}
      {/* 📊 CONSOLE DE DONNÉES - Stats & Badges en mode spatial */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="max-w-4xl mx-auto mb-6 stats-section"
        data-section="stats"
      >
        {/* Header cliquable style HUD */}
        <motion.button
          onClick={() => setShowStats(!showStats)}
          className="w-full relative overflow-hidden text-left transition-all"
          style={{
            background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #312e81 100%)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: showStats ? '1rem 1rem 0 0' : '1rem',
            boxShadow: '0 0 30px rgba(139, 92, 246, 0.15)'
          }}
          whileHover={{ scale: 1.005 }}
          whileTap={{ scale: 0.995 }}
        >
          {/* Étoiles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(10)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 rounded-full bg-purple-400"
                style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.1 }}
                animate={{ opacity: [0.1, 0.4, 0.1] }}
                transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
              />
            ))}
          </div>
          
          {/* HUD Corners */}
          <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-purple-500/50" />
          <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-purple-500/50" />
          
          <div className="relative p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <motion.div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)', border: '1px solid rgba(139, 92, 246, 0.3)' }}
                  animate={{ boxShadow: ['0 0 10px rgba(139, 92, 246, 0.2)', '0 0 20px rgba(139, 92, 246, 0.4)', '0 0 10px rgba(139, 92, 246, 0.2)'] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <span className="text-xl">📊</span>
                </motion.div>
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    Console de Données
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
                      ANALYTICS
                    </span>
                  </h3>
                  <p className="text-[11px] text-purple-500/70 font-mono tracking-wider">
                    BADGES • RECORDS • PERFORMANCES
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Toggle son */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newState = !soundSystem.isEnabled();
                    soundSystem.setEnabled(newState);
                    if (newState) {
                      soundSystem.playClick();
                      toast.success('🔊 Sons activés !');
                    } else {
                      toast('🔇 Sons désactivés');
                    }
                  }}
                  className={`p-2 rounded-lg transition-all ${
                    soundSystem.isEnabled()
                      ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 border border-purple-500/30'
                      : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 border border-gray-500/30'
                  }`}
                  title={soundSystem.isEnabled() ? 'Désactiver les sons' : 'Activer les sons'}
                >
                  {soundSystem.isEnabled() ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </button>
                
                <motion.div
                  animate={{ rotate: showStats ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20"
                >
                  <ChevronDown className="w-5 h-5 text-purple-400" />
                </motion.div>
              </div>
            </div>
          </div>
        </motion.button>

        {/* Contenu dépliable */}
        <AnimatePresence>
          {showStats && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div 
                className="relative p-6 pt-4"
                style={{
                  background: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)',
                  borderLeft: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRight: '1px solid rgba(139, 92, 246, 0.3)',
                  borderBottom: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '0 0 1rem 1rem'
                }}
              >
                {/* HUD Corners bas */}
                <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-purple-500/30" />
                <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-purple-500/30" />
                
                {/* Stats personnelles */}
                <PersonalStats customer={customer} cards={cards} isDarkMode={isDarkMode} />
                
                {/* Séparateur */}
                <div className="my-6 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
                
                {/* Badges */}
                <BadgesGrid customer={customer} cards={cards} isDarkMode={isDarkMode} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* 🔐 COFFRE-FORT SÉCURISÉ - CARTES DE FIDÉLITÉ */}
      <div data-section="vault" className="secure-vault-section">
        <SecureVault 
          cards={cards} 
          customerPin={customer?.pin || '1234'} 
          isDarkMode={isDarkMode} 
        />
      </div>

      {/* 🏆 CLASSEMENT CLIENT-MALIN - Après les cartes de fidélité */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="max-w-4xl mx-auto mt-6 leaderboard-section"
        data-section="leaderboard"
      >
        <CustomerLeaderboard currentCustomerId={customer?.id} />
      </motion.div>

      {/* Message de bienvenue - Esprit famille ULTRA FUN */}
      <FamilyMessage isDarkMode={isDarkMode} />

      {/* Footer MAGNIFIQUE - Le bloc final spectaculaire "La Petite Épicerie qui vous choute" */}
      <FooterSection isDarkMode={isDarkMode} />

      {/* 💜 HEARTBEATS - Et mon cœur bat plus fort - POSITION 12 : Tout en bas après le footer */}
      {/* 👻 Mode Confidentiel : caché si activé, triple-tap sur avatar pour ouvrir */}
      {!localStorage.getItem(`heartbeats_confidential_${customer?.id}`) || localStorage.getItem(`heartbeats_confidential_${customer?.id}`) !== 'true' ? (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
        className="max-w-4xl mx-auto mb-6"
      >
        <motion.div
          className="relative overflow-hidden rounded-2xl p-6 border-2"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(236, 72, 153, 0.15) 50%, rgba(255, 107, 107, 0.15) 100%)',
            borderColor: 'rgba(236, 72, 153, 0.5)',
            boxShadow: '0 0 40px rgba(236, 72, 153, 0.3), 0 0 80px rgba(139, 92, 246, 0.2)'
          }}
          animate={{
            boxShadow: [
              '0 0 40px rgba(236, 72, 153, 0.3), 0 0 80px rgba(139, 92, 246, 0.2)',
              '0 0 60px rgba(236, 72, 153, 0.5), 0 0 100px rgba(139, 92, 246, 0.3)',
              '0 0 40px rgba(236, 72, 153, 0.3), 0 0 80px rgba(139, 92, 246, 0.2)',
            ]
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {/* Hearts floating animation */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute text-2xl opacity-30"
                initial={{ x: `${20 + i * 15}%`, y: '100%', rotate: 0 }}
                animate={{ y: '-20%', rotate: [0, 10, -10, 0], opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 4 + i * 0.5, repeat: Infinity, delay: i * 0.8, ease: "easeOut" }}
              >
                💜
              </motion.div>
            ))}
          </div>

          <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
            {/* 🧚 GRAND VISAGE DE PIXIE */}
            <motion.div
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="flex-shrink-0 relative"
            >
              <div className="absolute inset-0 rounded-full blur-2xl opacity-40 pointer-events-none" style={{ background: 'rgba(236, 72, 153, 0.6)', transform: 'scale(1.2)' }} />
              <img 
                src="https://customer-assets.emergentagent.com/job_heartbeats-revamp/artifacts/jybu2wd3_88835866-396a-4af5-8f1b-1ff67b42abbe.png" 
                alt="Pixie" 
                className="relative w-24 h-24 md:w-32 md:h-32 rounded-full object-cover shadow-2xl"
                style={{ boxShadow: '0 0 40px rgba(236, 72, 153, 0.5)' }}
              />
            </motion.div>

            <div className="flex-1 text-center md:text-left">
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-lg md:text-xl font-medium bg-gradient-to-r from-pink-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-2"
              >
                {(() => {
                  const hooks = [
                    `Coucou ${customer?.firstName || 'toi'} ! 😏 Envie de papoter autour d'un bon café ?`,
                    `Salut ${customer?.firstName || 'beau gosse'} ! Des gens sympas t'attendent au salon... Tu viens ?`,
                    `Hey ${customer?.firstName || 'toi'} ! Rompre la solitude, refaire le monde... C'est par ici 💜`,
                    `${customer?.firstName || 'Mon chou'}, j'ai des gens sympas à te présenter ! Entre, on t'attend 😘`,
                  ];
                  return hooks[Math.floor(Date.now() / 60000) % hooks.length];
                })()}
              </motion.p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                💬 Papotez entre amis, dans une oasis de positivité !
              </p>
              
              <div className="flex items-center gap-2 mt-2 opacity-60">
                <img src="https://static.prod-images.emergentagent.com/jobs/2a092d8c-83c7-4c9e-ac69-8947dad50218/images/0b5e9362a5bf157caeeee00c101dc4b566cce930b468c990a9f498a5fbd16474.png" alt="Heartbeats" className="w-5 h-5" />
                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Et mon cœur bat plus fort</span>
              </div>
            </div>

            <MagneticButton
              message={magneticHook.magneticMessage}
              onClick={() => { magneticHook.trackOpen(); setShowHeartbeats(true); }}
              isHighUrgency={magneticHook.isHighUrgency}
              isDarkMode={isDarkMode}
            />
          </div>
        </motion.div>
      </motion.div>
      ) : null}

      {/* 🙈 Popup de rétention magnétique */}
      <RetentionPopup
        isOpen={magneticHook.showRetentionPopup}
        data={magneticHook.retentionData}
        onStay={() => { magneticHook.cancelExit(); }}
        onLeave={() => { magneticHook.confirmExit(); setShowHeartbeats(false); }}
        isDarkMode={isDarkMode}
      />

      {/* Profile Editor Modal */}
      {showProfileEditor && (
        <ProfileEditor
          customer={customer}
          onUpdate={(updatedCustomer) => setCustomer(updatedCustomer)}
          onClose={() => setShowProfileEditor(false)}
        />
      )}

      {/* Parental Consent Modal */}
      {showParentalConsent && (
        <ParentalConsentModal
          customer={customer}
          onUpdate={(updatedCustomer) => setCustomer(updatedCustomer)}
          onClose={() => setShowParentalConsent(false)}
        />
      )}

      {/* 2FA Verification Modal (login) */}
      {show2FAVerification && pending2FACustomer && (
        <TOTPLoginVerification
          customerId={pending2FACustomer.id}
          customerName={pending2FACustomer.name}
          onSuccess={handle2FASuccess}
          onCancel={() => {
            setShow2FAVerification(false);
            setPending2FACustomer(null);
            setPin(['', '', '', '']);
          }}
        />
      )}

      {/* 2FA Setup Modal */}
      {show2FASetup && customer && (
        <TOTPSetupModal
          customerId={customer.id}
          onSuccess={() => setTotpStatus({ enabled: true, verified: true })}
          onClose={() => setShow2FASetup(false)}
        />
      )}

      {/* Guide 2FA Modal */}
      {show2FAGuide && (
        <Dialog open={show2FAGuide} onOpenChange={setShow2FAGuide}>
          <DialogContent className="sm:max-w-lg bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-blue-500/50 text-white max-h-[90vh] overflow-y-auto w-[95vw] max-w-[95vw] sm:w-auto sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-bold text-center flex items-center justify-center gap-2">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-green-400 flex-shrink-0" />
                <span className="truncate">Sécuriser mon compte</span>
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              {/* Pourquoi ? */}
              <div className="bg-green-500/10 rounded-xl p-3 sm:p-4 border border-green-500/30">
                <h3 className="font-bold text-green-400 mb-2 flex items-center gap-2 text-sm sm:text-base">
                  <KeyRound className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  Pourquoi activer le 2FA ?
                </h3>
                <ul className="text-xs sm:text-sm text-gray-300 space-y-1">
                  <li>✅ <strong>Protège vos tampons</strong> même si on connaît votre PIN</li>
                  <li>✅ <strong>Vous seul</strong> accédez à votre compte</li>
                  <li>✅ <strong>Gratuit</strong> - 2 minutes pour configurer</li>
                </ul>
              </div>

              {/* Comment ça marche */}
              <div className="bg-blue-500/10 rounded-xl p-3 sm:p-4 border border-blue-500/30">
                <h3 className="font-bold text-blue-400 mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <Smartphone className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  Comment faire ? (2 min)
                </h3>
                
                <div className="space-y-3">
                  <div className="flex gap-2 sm:gap-3 items-start">
                    <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-500 text-white text-xs sm:text-sm font-bold flex items-center justify-center flex-shrink-0">1</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs sm:text-sm font-medium text-white">Téléchargez Google Authenticator</p>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        <a 
                          href="https://apps.apple.com/app/google-authenticator/id388497605" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded transition-colors inline-flex items-center gap-1"
                        >
                          📱 iPhone
                        </a>
                        <a 
                          href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded transition-colors inline-flex items-center gap-1"
                        >
                          🤖 Android
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 sm:gap-3 items-start">
                    <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-500 text-white text-xs sm:text-sm font-bold flex items-center justify-center flex-shrink-0">2</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs sm:text-sm font-medium text-white">Scannez le QR code</p>
                      <p className="text-xs text-gray-400">Cliquez &quot;Activer&quot; puis scannez avec l&apos;app</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 sm:gap-3 items-start">
                    <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-yellow-500 text-white text-xs sm:text-sm font-bold flex items-center justify-center flex-shrink-0">!</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs sm:text-sm font-medium text-yellow-400">Notez le code secret</p>
                      <p className="text-xs text-gray-400">Gardez-le pour récupérer l&apos;accès si vous perdez votre tél.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 sm:gap-3 items-start">
                    <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-500 text-white text-xs sm:text-sm font-bold flex items-center justify-center flex-shrink-0">3</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs sm:text-sm font-medium text-white">Entrez le code à 6 chiffres</p>
                      <p className="text-xs text-gray-400">Nouveau code toutes les 30 sec</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 sm:gap-3 items-start">
                    <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-green-500 text-white text-xs sm:text-sm font-bold flex items-center justify-center flex-shrink-0">✓</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs sm:text-sm font-medium text-green-400">C&apos;est fait !</p>
                      <p className="text-xs text-gray-400">Entrez le code de l&apos;app à chaque connexion</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Info perte téléphone */}
              <div className="bg-orange-500/10 rounded-xl p-3 border border-orange-500/30">
                <p className="text-orange-400 text-xs text-center">
                  📱 <strong>Tél. perdu ?</strong> Contactez l&apos;épicerie pour désactiver le 2FA.
                </p>
              </div>

              {/* Boutons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    // Générer et télécharger le PDF
                    const pdfContent = `
GUIDE : SÉCURISER MON ESPACE CLIENT-MALIN
==========================================

🔒 POURQUOI ACTIVER LA DOUBLE AUTHENTIFICATION ?

• Protège vos tampons : même si quelqu'un connaît votre code PIN, il ne pourra pas accéder à votre compte sans votre téléphone.
• Vous seul pouvez accéder à votre compte.
• C'est gratuit et prend seulement 2 minutes.

📱 COMMENT FAIRE ? (2 minutes)

ÉTAPE 1 : Téléchargez l'application "Google Authenticator"
   - iPhone : App Store → chercher "Google Authenticator"
   - Android : Play Store → chercher "Google Authenticator"

ÉTAPE 2 : Dans votre espace Client-Malin, cliquez sur "Activer" à côté de 2FA

ÉTAPE 3 : Scannez le QR code affiché avec l'application Google Authenticator

⚠️ IMPORTANT : NOTEZ LE CODE SECRET !
   Un code secret s'affiche sous le QR code. Notez-le et gardez-le en lieu sûr.
   Si vous perdez votre téléphone, ce code vous permettra de récupérer l'accès.

ÉTAPE 4 : Entrez le code à 6 chiffres généré par l'application

✅ C'EST FAIT ! 

À chaque connexion, l'application génèrera un nouveau code à 6 chiffres que vous devrez entrer. Ce code change toutes les 30 secondes.

📱 TÉLÉPHONE PERDU ?
Si vous perdez votre téléphone et n'avez pas votre code secret, contactez l'épicerie.
Un vendeur pourra désactiver le 2FA pour vous permettre de vous reconnecter.

==========================================
La Petite Épicerie - Programme Client-Malin
www.la-petite-epicerie.shop
                    `;
                    
                    const blob = new Blob([pdfContent], { type: 'text/plain;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'Guide_Securite_2FA_Client-Malin.txt';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex-1 border-gray-500 text-gray-300 hover:bg-gray-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger le guide
                </Button>
                
                {!totpStatus?.enabled && (
                  <Button
                    onClick={() => {
                      setShow2FAGuide(false);
                      setShow2FASetup(true);
                    }}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Activer maintenant
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal Jeux */}
      <Dialog open={showGames} onOpenChange={setShowGames}>
        <DialogContent className="w-[90vw] max-w-[500px] sm:max-w-[600px] bg-gradient-to-br from-slate-900 to-purple-900 border-2 border-purple-500/50 text-white max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center flex items-center justify-center gap-2">
              <Gamepad2 className="w-6 h-6 text-purple-400" />
              🎮 Jeux LPE
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 px-1 w-full overflow-hidden">
            <p className="text-center text-gray-400 text-sm mb-4">
              Tentez votre chance et gagnez des récompenses ! 🎁
            </p>
            <GamesHub customerId={customer?.id} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat LPE */}
      <LPEChat 
        customerId={customer?.id}
        customerName={customer?.name}
        isOpen={showChat}
        onClose={() => setShowChat(false)}
      />

      {/* ✨ PIXIE INFILTRATOR - DÉSACTIVÉ TEMPORAIREMENT */}
      {/* <PixieInfiltrator
        isEnabled={!showHeartbeats && customer?.id}
        customerId={customer?.id}
        customerName={extractFirstName(customer?.name || customer?.firstName)}
        hasHeartbeatsProfile={!!heartbeatsProfile}
        isCouple={heartbeatsProfile?.relationshipStatus === 'couple'}
        matchCount={heartbeatsProfile?.matchCount || 0}
        nearbyCount={54}
        onGoToHeartbeats={() => setShowHeartbeats(true)}
        delayBeforeAppear={180000}
        isDarkMode={isDarkMode}
      /> */}

      {/* 💜 Modal Heartbeats */}
      <Dialog open={showHeartbeats} onOpenChange={(open) => {
        if (!open) {
          // Fermeture directe sans popup de rétention
          setShowHeartbeats(false);
        } else {
          setShowHeartbeats(true);
        }
      }}>
        <DialogContent className={`sm:max-w-2xl max-h-[95vh] overflow-y-auto p-0 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white'}`}>
          <Heartbeats 
            customer={customer}
            isDarkMode={isDarkMode}
            onClose={() => setShowHeartbeats(false)}
            onProfileUpdate={(profile) => setHeartbeatsProfile(profile)}
          />
        </DialogContent>
      </Dialog>

      {/* Bouton Chat flottant */}
      <ChatButton onClick={() => setShowChat(!showChat)} />

      {/* Modal de livraison classique (backup) */}
      <DeliveryOrderModal
        isOpen={showDeliveryModal}
        customer={customer}
        onClose={() => setShowDeliveryModal(false)}
      />

      {/* 🛵 Max - Assistant Livraison Intelligent */}
      <MaxDeliveryAssistant
        isOpen={showMaxAssistant}
        customer={customer}
        onClose={() => setShowMaxAssistant(false)}
        onOrderSubmit={() => {
          toast.success('🛵 Commande envoyée avec Max !');
        }}
      />

      {/* 💳 Modal Stripe Payment */}
      {showStripePayment && customer && (
        <StripePayment
          customerId={customer.id}
          onClose={() => setShowStripePayment(false)}
          onSuccess={() => {
            setShowStripePayment(false);
            toast.success('🎉 Achat réussi !');
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Footer compact supprimé - Redondant avec FooterSection spatial */}
    </div>
  );
}
// Build 1767376597
